import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LayoutMasterDetail from "layouts/LayoutMasterDetail";

import { StoreContext } from "shared/context/store-context";
import { PERMISSION_TYPES } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";

import InventoryAccidentItem from "./InventoryAccidentItem";

import { formatYearMonthDateWithTimezone } from "shared/utility/date";

const InventoryAccidents = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const itemType = "accident";
  const listName = "inventory_accidents";

  const { accidents, DeleteAccident, UpdateAccident, InsertAccident, vehicles, driverOptions } =
    useContext(StoreContext);

  const [pageItems, setPageItems] = useState();

  useEffect(() => {
    setPageItems(accidents);
  }, [accidents]);

  const listColumns = [
    {
      field: "vehicle",
      headerName: t("inventoryAccident.columns.vehicle"),
      width: 200,
      renderCell: (params) => {
        const vehicleId = params.value;
        const vehicle = vehicles.find((v) => v.id === vehicleId);
        return vehicle?.plate ? `${vehicle.plate}` : "-";
      },
    },

    {
      field: "accidentDate",
      headerName: t("inventoryAccident.columns.accidentDate"),
      width: 150,
      renderCell: (params) => {
        return formatYearMonthDateWithTimezone(params.value);
      },
    },

    {
      field: "driver",
      headerName: t("inventoryAccident.columns.driver"),
      width: 200,
      renderCell: (params) => {
        const driverId = params.value;
        if (!driverId) return "-";
        return driverOptions.find((d) => d.value === driverId)?.label || "-";
      },
    },

    {
      field: "accidentLocation",
      headerName: t("inventoryAccident.columns.accidentLocation"),
      width: 200,
    },

    {
      field: "accidentType",
      headerName: t("inventoryAccident.columns.accidentType"),
      width: 150,
      renderCell: (params) => {
        const type = params.value;
        const typeMap = {
          çarpışma: t("inventoryAccident.accidentTypes.collision"),
          devrilme: t("inventoryAccident.accidentTypes.rollover"),
          yanma: t("inventoryAccident.accidentTypes.fire"),
          "suya düşme": t("inventoryAccident.accidentTypes.water"),
          diğer: t("inventoryAccident.accidentTypes.other"),
        };
        return typeMap[type] || type || "-";
      },
    },

    {
      field: "severity",
      headerName: t("inventoryAccident.columns.severity"),
      width: 120,
      renderCell: (params) => {
        const severity = params.value;
        const severityMap = {
          hafif: t("inventoryAccident.severity.light"),
          orta: t("inventoryAccident.severity.medium"),
          ağır: t("inventoryAccident.severity.heavy"),
          "çok ağır": t("inventoryAccident.severity.veryHeavy"),
        };
        return severityMap[severity] || severity || "-";
      },
    },

    {
      field: "vehicleStatus",
      headerName: t("inventoryAccident.columns.vehicleStatus"),
      width: 150,
      renderCell: (params) => {
        const status = params.value;
        const statusMap = {
          kullanılabilir: t("inventoryAccident.vehicleStatus.usable"),
          "tamir ediliyor": t("inventoryAccident.vehicleStatus.underRepair"),
          hurda: t("inventoryAccident.vehicleStatus.scrap"),
          satıldı: t("inventoryAccident.vehicleStatus.sold"),
        };
        return statusMap[status] || status || "-";
      },
    },

    {
      field: "actualDamageCost",
      headerName: t("inventoryAccident.columns.actualDamageCost"),
      width: 150,
      renderCell: (params) => {
        return params.value ? `${params.value} ₺` : "-";
      },
    },

    {
      field: "companyPayment",
      headerName: t("inventoryAccident.columns.companyPayment"),
      width: 150,
      renderCell: (params) => {
        return params.value ? `${params.value} ₺` : "-";
      },
    },

    {
      field: "insuranceAmount",
      headerName: t("inventoryAccident.columns.insuranceAmount"),
      width: 150,
      renderCell: (params) => {
        return params.value ? `${params.value} ₺` : "-";
      },
    },

    {
      field: "driverPayment",
      headerName: t("inventoryAccident.columns.driverPayment"),
      width: 150,
      renderCell: (params) => {
        return params.value ? `${params.value} ₺` : "-";
      },
    },

    {
      field: "passengersInjured",
      headerName: t("inventoryAccident.columns.passengersInjured"),
      width: 120,
    },

    {
      field: "passengersDeceased",
      headerName: t("inventoryAccident.columns.passengersDeceased"),
      width: 120,
    },

    {
      field: "repairWorkshop",
      headerName: t("inventoryAccident.columns.repairWorkshop"),
      width: 150,
    },

    {
      field: "counterpartyName",
      headerName: t("inventoryAccident.columns.counterpartyName"),
      width: 150,
    },

    {
      field: "counterpartyPhone",
      headerName: t("inventoryAccident.columns.counterpartyPhone"),
      width: 150,
    },

    {
      field: "counterpartyAddress",
      headerName: t("inventoryAccident.columns.counterpartyAddress"),
      width: 200,
    },

    {
      field: "notes",
      headerName: t("inventoryAccident.columns.notes"),
      width: 200,
    },
  ];

  const standardHelpItems = [
    t("inventoryAccident.help.standard1"),
    t("inventoryAccident.help.standard2"),
    t("inventoryAccident.help.standard3"),
    t("inventoryAccident.help.standard4"),
  ];

  const highlightedHelpItems = [
    t("inventoryAccident.help.highlighted1"),
    t("inventoryAccident.help.highlighted2"),
    t("inventoryAccident.help.highlighted3"),
    t("inventoryAccident.help.highlighted4"),
    t("inventoryAccident.help.highlighted5"),
  ];

  return (
    <LayoutMasterDetail
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      hideCreateNewItem={!auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY]}
      SelectedItemComponent={InventoryAccidentItem}
      NewItemComponent={InventoryAccidentItem}
      DeleteItem={DeleteAccident}
      InsertItem={InsertAccident}
      UpdateItem={UpdateAccident}
      standardHelpItems={standardHelpItems}
      highlightedHelpItems={highlightedHelpItems}
      hideCreateNewItemWhenSelected={true}
    />
  );
};

export default InventoryAccidents;
